/**
 * Error Tracking - Error monitoring with optional Sentry integration
 * Sentry is optional - if not installed, errors will be logged to console
 */

// Error tracking configuration
const config = {
  enabled: import.meta.env.PROD,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE || 'development',
  release: import.meta.env.VITE_APP_VERSION || '0.1.0',
}

let Sentry = null
let sentryInitialized = false

// Initialize Sentry (if configured and available)
export async function initErrorTracking() {
  if (sentryInitialized) return
  
  if (!config.enabled || !config.dsn) {
    console.debug('[ErrorTracking] Sentry not configured - errors will be logged locally')
    return
  }

  try {
    // Use a dynamic import with a different approach to avoid Vite resolution issues
    const sentryModule = await import(/* @vite-ignore */ '@sentry/react').catch(() => null)
    
    if (!sentryModule) {
      console.debug('[ErrorTracking] @sentry/react not installed - errors will be logged locally')
      return
    }
    
    Sentry = sentryModule.default || sentryModule
    
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: config.release,
      tracesSampleRate: 0.2,
      integrations: [
        new Sentry.BrowserTracing({
          tracePropagationTargets: ['localhost', /^https:\/\/[^/]*\.supabase\.co/],
        }),
        new Sentry.Replay({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      replaysOnErrorSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
    })
    
    sentryInitialized = true
    console.debug('[ErrorTracking] Sentry initialized successfully')
  } catch (err) {
    console.debug('[ErrorTracking] Failed to initialize Sentry:', err.message)
  }
}

// Track error
export function trackError(error, context = {}) {
  try {
    // Sentry (if available)
    if (sentryInitialized && Sentry) {
      Sentry.captureException(error, {
        extra: context,
      })
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('[Error]', error, context)
    }

    // Send to custom endpoint if configured
    if (import.meta.env.VITE_ERROR_TRACKING_ENDPOINT) {
      const payload = {
        error: {
          message: error.message || 'Unknown error',
          stack: error.stack,
          name: error.name || 'Error',
        },
        context,
        timestamp: new Date().toISOString(),
        url: window?.location?.href || 'unknown',
        userAgent: navigator?.userAgent || 'unknown',
      }
      
      try {
        navigator.sendBeacon
          ? navigator.sendBeacon(
              import.meta.env.VITE_ERROR_TRACKING_ENDPOINT,
              JSON.stringify(payload)
            )
          : fetch(import.meta.env.VITE_ERROR_TRACKING_ENDPOINT, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
              keepalive: true,
            }).catch(() => {})
      } catch (beaconErr) {
        // Silently fail if beacon fails
      }
    }
  } catch (err) {
    // Silently fail to avoid infinite loops
  }
}

// Track API error
export function trackApiError(error, endpoint, method, status) {
  trackError(error, {
    type: 'api_error',
    endpoint,
    method,
    status,
    timestamp: new Date().toISOString(),
  })
}

// Track error with component context
export function trackComponentError(error, componentName, props = {}) {
  trackError(error, {
    type: 'component_error',
    component: componentName,
    props: Object.keys(props).slice(0, 5), // Only track prop names, not values
    timestamp: new Date().toISOString(),
  })
}

// User feedback
export function captureUserFeedback({ name, email, message, context = {} }) {
  try {
    if (sentryInitialized && Sentry) {
      Sentry.captureMessage('User Feedback', {
        level: 'info',
        extra: {
          name,
          email,
          message,
          ...context,
        },
      })
    }
  } catch (err) {
    // Silently fail
  }
}

// Set user context for error tracking
export function setUserContext(user) {
  try {
    if (sentryInitialized && Sentry) {
      Sentry.setUser({
        id: user?.id,
        email: user?.email,
        username: user?.name || user?.email,
      })
    }
  } catch (err) {
    // Silently fail
  }
}

// Clear user context
export function clearUserContext() {
  try {
    if (sentryInitialized && Sentry) {
      Sentry.setUser(null)
    }
  } catch (err) {
    // Silently fail
  }
}

export default {
  initErrorTracking,
  trackError,
  trackApiError,
  trackComponentError,
  captureUserFeedback,
  setUserContext,
  clearUserContext,
}